import { AllAccountsCard } from "./components/AllAccountsCard";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { BillsDashboardCard } from "./components/BillsDashboardCard";
import { DashboardSummaryComponent } from "./components/DashboardSummary";
import { MonthlySpendingSummary } from "./components/MonthlySpendingSummary";
import { Button } from "@groot/ui/button";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { useAccounts } from "@/hooks/api";
import { useDashboardSummary, useMonthlySpending, useRefreshDashboard } from "./hooks/useDashboard";
import { toast } from "@/lib/toast";
import { RefreshCw } from "lucide-react";
import { useState } from "react";

export function Dashboard() {
  // Initialize with current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  };

  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());

  const { data: summary, isLoading: isLoadingSummary, error: summaryError } = useDashboardSummary();
  const {
    data: monthlySpending,
    isLoading: isLoadingMonthly,
    error: monthlyError,
  } = useMonthlySpending(selectedMonth);
  const { data: accounts = [], isLoading: isLoadingAccounts } = useAccounts();
  const refreshDashboard = useRefreshDashboard();

  const isLoading = isLoadingSummary || isLoadingMonthly;
  const error = summaryError || monthlyError;

  const handleRefresh = async () => {
    try {
      await refreshDashboard();
      toast({
        title: "Dashboard refreshed",
        description: "All data has been updated",
      });
    } catch {
      toast({
        title: "Refresh failed",
        description: "Failed to refresh dashboard data",
        variant: "destructive",
      });
    }
  };

  if (isLoading && !summary && !monthlySpending) {
    return (
      <Page>
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Dashboard"
        description="Overview of your financial accounts and activity"
        actions={
          <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className="size-4" />
            Refresh
          </Button>
        }
        className="mb-8"
      />

      {/* Error Banner */}
      {error && (
        <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/10 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium text-destructive">Error loading dashboard data</p>
              <p className="mt-1 text-sm text-destructive">
                {error instanceof Error ? error.message : "An error occurred"}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
              <RefreshCw className="size-4" />
              Retry
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Key Metrics */}
        <div>
          <h2 className="mb-4 text-sm font-medium text-foreground">Key Metrics</h2>
          <DashboardSummaryComponent data={summary} isLoading={isLoadingSummary} />
        </div>

        {/* Accounts Overview and Monthly Spending */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <AllAccountsCard accounts={accounts} isLoading={isLoadingAccounts} />
          </div>

          <div>
            <MonthlySpendingSummary
              data={monthlySpending}
              isLoading={isLoadingMonthly}
              selectedMonth={selectedMonth}
              onMonthChange={setSelectedMonth}
            />
          </div>
        </div>

        {/* Quick Access */}
        <div className="pt-6 border-t border-border">
          <h2 className="mb-4 text-sm font-medium text-foreground">Quick Access</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <BillsDashboardCard />
          </div>
        </div>
      </div>
    </Page>
  );
}
